import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "button", "message"]
  static values = { 
    cooldown: { type: Number, default: 300000 }, // 5 minutes in milliseconds
    maxAttempts: { type: Number, default: 3 }
  }

  connect() {
    this.checkRateLimit()
  }

  checkRateLimit() {
    const attempts = this.getAttempts()
    const lastAttempt = this.getLastAttempt()
    const now = Date.now()

    // Reset attempts if cooldown period has passed
    if (lastAttempt && (now - lastAttempt) > this.cooldownValue) {
      this.clearAttempts()
      return
    }

    // Check if user has exceeded max attempts
    if (attempts >= this.maxAttemptsValue) {
      this.disableForm("Too many attempts. Please try again later.")
      
      // Calculate remaining cooldown time
      const remainingTime = Math.ceil((this.cooldownValue - (now - lastAttempt)) / 1000 / 60)
      this.showMessage(`Rate limit exceeded. Try again in ${remainingTime} minutes.`, "warning")
    }
  }

  beforeSubmit(event) {
    const attempts = this.getAttempts()
    
    if (attempts >= this.maxAttemptsValue) {
      event.preventDefault()
      this.showMessage("Rate limit exceeded. Please wait before trying again.", "error")
      return false
    }

    // Record this attempt
    this.recordAttempt()
    
    // Show loading state
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Processing...
      `
    }

    return true
  }

  getAttempts() {
    const stored = localStorage.getItem('company_claim_attempts')
    return stored ? parseInt(stored) : 0
  }

  getLastAttempt() {
    const stored = localStorage.getItem('company_claim_last_attempt')
    return stored ? parseInt(stored) : null
  }

  recordAttempt() {
    const attempts = this.getAttempts() + 1
    localStorage.setItem('company_claim_attempts', attempts.toString())
    localStorage.setItem('company_claim_last_attempt', Date.now().toString())
  }

  clearAttempts() {
    localStorage.removeItem('company_claim_attempts')
    localStorage.removeItem('company_claim_last_attempt')
  }

  disableForm(reason) {
    if (this.hasFormTarget) {
      const inputs = this.formTarget.querySelectorAll('input, button, select, textarea')
      inputs.forEach(input => input.disabled = true)
    }
    
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.title = reason
    }
  }

  showMessage(text, type = "info") {
    if (this.hasMessageTarget) {
      this.messageTarget.innerHTML = `
        <div class="alert alert-${type === 'error' ? 'danger' : type} alert-dismissible fade show" role="alert">
          ${text}
          <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        </div>
      `
    }
  }

  // Handle successful claim (called from server response)
  onSuccess() {
    this.clearAttempts()
  }

  // Handle failed claim (called from server response)
  onError() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.75rem; height: 0.75rem; margin-right: 0.25rem;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
        Claim Company
      `
    }
  }
}